import type { Product } from '@/types/product';
import { CJ925_TRIAL_ITEMS, type CJ925TrialItem } from '@/data/cj925Trial';
import { CJ925_MEDIA } from '@/data/cj925Media';

const PRICING_STORAGE_KEY='mielley_cj925_preview_v2';
const DRAFT_STORAGE_KEY='mielley_cj925_preview_edits_v23';

type Settings={divisor:number;vatRate:number;multiplier:number};
export type CJ925CatalogMatchRecord={row:number;sku:string;spec:string;catalogPrice:number;source:'manual'|'suggested'|'exact'};
type PricingState={prices:Record<string,number|string>;settings:Settings;matches:Record<string,CJ925CatalogMatchRecord>};
type DraftMap=Record<string,Product>;

const DEFAULT_SETTINGS:Settings={divisor:4,vatRate:18,multiplier:2};
const round2=(n:number)=>Math.round((n+Number.EPSILON)*100)/100;

const safeParse=<T,>(value:string|null,fallback:T):T=>{
 try{return value?JSON.parse(value) as T:fallback}catch{return fallback}
};

const categoryMeta=(category:CJ925TrialItem['category'])=>({
 bracelets:{label:'צמידים',subcategory:'צמידי מוסונייט'},
 earrings:{label:'עגילים',subcategory:'עגילי מוסונייט'},
 necklaces:{label:'שרשראות',subcategory:'שרשראות מוסונייט'},
 rings:{label:'טבעות',subcategory:'טבעות מוסונייט'}
} as const)[category];

export function loadCJ925PricingState():PricingState{
 if(typeof window==='undefined')return {prices:{},settings:DEFAULT_SETTINGS,matches:{}};
 const raw=safeParse<Partial<PricingState>>(localStorage.getItem(PRICING_STORAGE_KEY),{});
 return {prices:raw.prices||{},settings:{...DEFAULT_SETTINGS,...(raw.settings||{})},matches:raw.matches||{}};
}

function loadDraftMap():DraftMap{
 if(typeof window==='undefined')return {};
 return safeParse<DraftMap>(localStorage.getItem(DRAFT_STORAGE_KEY),{});
}

function saveDraftMap(map:DraftMap){
 if(typeof window!=='undefined')localStorage.setItem(DRAFT_STORAGE_KEY,JSON.stringify(map));
}

function itemById(productId:number){return CJ925_TRIAL_ITEMS.find(x=>x.productId===productId)}

function ringSizes(item:CJ925TrialItem){
 if(item.category!=='rings')return [];
 return item.variants.map(v=>v.replace(/^US\s*/i,'').trim()).filter(Boolean);
}

function lengthOptions(item:CJ925TrialItem){
 if(item.category==='rings')return [];
 return item.variants
  .map(v=>v.match(/\d+(?:\.\d+)?\s*(?:cm|ס״מ|סמ)/i)?.[0]||'')
  .filter(Boolean);
}

function pricingFor(item:CJ925TrialItem){
 const state=loadCJ925PricingState();
 const catalog=Number(state.prices[String(item.productId)]||state.prices[item.productId as unknown as string]||0)||0;
 const divisor=state.settings.divisor>0?state.settings.divisor:4;
 const supplierNet=catalog/divisor;
 const supplierGross=supplierNet*(1+state.settings.vatRate/100);
 const sale=round2(supplierGross*state.settings.multiplier);
 return {catalog,supplierNet,supplierGross,sale,settings:state.settings};
}

function sourceGallery(item:CJ925TrialItem){
 const media=CJ925_MEDIA[item.productId]||[];
 return media.length?media:[item.previewImage||'/assets/diamond-placeholder.svg'];
}

function isLegacyCJ925Media(url:string){
 return /cj925\.com\/images\/thumbnails\/50\/50\//i.test(url);
}

function sameList(a:string[]|undefined,b:string[]){return JSON.stringify(a||[])===JSON.stringify(b)}
function normalizePreviewVariants(saved:Product,item:CJ925TrialItem){
 const category=saved.category||item.category;
 const isRing=category==='rings';
 const supportsLength=category==='bracelets'||category==='necklaces';
 let lengths=supportsLength?(saved.lengthOptions||[]):[];
 // V2.3 editor showed a generic 16/17/18cm example on every product. If that exact
 // placeholder-like list was persisted for a source item that has no verified length
 // variation, remove it automatically. Any other manual length edit is preserved.
 if(supportsLength&&item.variants.length===0&&sameList(lengths,['16cm','17cm','18cm']))lengths=[];
 return {...saved,ringSizeSystem:isRing?(saved.ringSizeSystem||'US'):undefined,ringSizes:isRing?(saved.ringSizes||[]):[],lengthOptions:lengths};
}

function upgradeSavedMedia(saved:Product,item:CJ925TrialItem){
 const fresh=sourceGallery(item);
 const current=(saved.gallery||[]).filter(Boolean);
 const onlyLegacy=current.length===0||current.every(isLegacyCJ925Media);
 if(!onlyLegacy)return saved;
 return {
  ...saved,
  gallery:fresh,
  image:fresh[0]||saved.image,
  hoverImage:fresh[1]||fresh[0]||saved.hoverImage,
  admin:{...(saved.admin||{}),sourceImageUrls:fresh}
 };
}

export function buildCJ925PreviewProduct(item:CJ925TrialItem):Product{
 const p=pricingFor(item);
 const meta=categoryMeta(item.category);
 const gallery=sourceGallery(item);
 const img=gallery[0]||'/assets/diamond-placeholder.svg';
 return {
  id:925000+item.productId,
  slug:`mielley-moissanite-${item.productId}`,
  name:item.name,
  sku:`MI-${item.productId}`,
  category:item.category,
  categoryKeys:[item.category],
  categoryLabel:meta.label,
  subcategory:meta.subcategory,
  price:p.sale,
  oldPrice:0,
  cost:p.supplierGross,
  shape:item.shape,
  carat:item.carat,
  color:item.color,
  clarity:item.clarity,
  cut:'Excellent',
  stone:'מוסונייט',
  metals:['כסף סטרלינג 925'],
  purities:['Rhodium'],
  gallery,
  image:img,
  hoverImage:gallery[1]||img,
  status:'draft',
  stockQty:0,
  lowStockThreshold:3,
  featured:false,
  readyToShip:false,
  certificateType:item.certificate||'GRA',
  certificateNumber:'',
  deliveryDays:'7–14 ימי עסקים',
  engraving:false,
  seoTitle:`${item.name} | MIELLEY`,
  seoDescription:`${item.name} מבית MIELLEY — תכשיט מוסונייט בגימור כסף סטרלינג 925 ורודיום.`,
  description:`${item.name} בעיצוב אלגנטי ונקי, משובץ מוסונייט באיכות D / VVS1 ומיועד למראה יוקרתי ובהיר.`,
  ringSizeSystem:item.category==='rings'?'US':undefined,
  ringSizes:ringSizes(item),
  lengthOptions:lengthOptions(item),
  admin:{
   supplier:'CJ925',
   supplierSku:item.supplierCode,
   supplierProductId:String(item.productId),
   supplierUrl:item.sourceUrl,
   catalogPrice:p.catalog,
   supplierNet:p.supplierNet,
   vatRate:p.settings.vatRate,
   supplierGross:p.supplierGross,
   priceMultiplier:p.settings.multiplier,
   lastSyncedAt:'',
   sourceImageUrls:gallery
  }
 };
}

export function loadCJ925PreviewProduct(productId:number):Product|null{
 const item=itemById(productId);
 if(!item)return null;
 const saved=loadDraftMap()[String(productId)];
 return saved?upgradeSavedMedia(normalizePreviewVariants(structuredClone(saved),item),item):buildCJ925PreviewProduct(item);
}

export function hasCJ925PreviewEdits(productId:number){
 return Boolean(loadDraftMap()[String(productId)]);
}

export function saveCJ925PreviewProduct(productId:number,product:Product){
 const map=loadDraftMap();
 const item=itemById(productId);
 map[String(productId)]=structuredClone(item?normalizePreviewVariants(product,item):product);
 saveDraftMap(map);

 // Keep the catalog-price field on the main CJ925 Preview screen in sync.
 const pricing=loadCJ925PricingState();
 pricing.prices[String(productId)]=Number(product.admin?.catalogPrice||0)||'';
 if(typeof window!=='undefined')localStorage.setItem(PRICING_STORAGE_KEY,JSON.stringify(pricing));
}

export function updateCJ925CatalogPrice(productId:number,value:number|string,settings?:Settings){
 if(typeof window==='undefined')return;
 const pricing=loadCJ925PricingState();
 if(settings)pricing.settings={...pricing.settings,...settings};
 pricing.prices[String(productId)]=value;
 localStorage.setItem(PRICING_STORAGE_KEY,JSON.stringify(pricing));

 const map=loadDraftMap();
 const saved=map[String(productId)];
 if(!saved)return;
 const catalog=Number(value||0)||0;
 const divisor=pricing.settings.divisor>0?pricing.settings.divisor:4;
 const supplierNet=catalog/divisor;
 const supplierGross=supplierNet*(1+pricing.settings.vatRate/100);
 const sale=round2(supplierGross*pricing.settings.multiplier);
 map[String(productId)]={
  ...saved,
  cost:supplierGross,
  price:sale,
  admin:{
   ...(saved.admin||{}),
   catalogPrice:catalog,
   supplierNet,
   vatRate:pricing.settings.vatRate,
   supplierGross,
   priceMultiplier:pricing.settings.multiplier
  }
 };
 saveDraftMap(map);
}

export function resetCJ925PreviewEdits(){
 if(typeof window!=='undefined')localStorage.removeItem(DRAFT_STORAGE_KEY);
}
